// Read the canonical stage label for a row that has an FK pointer at
// briefing_stages.
//
// Records returned from queries that embed `briefing_stages(label)`
// carry the canonical label on `record.briefing_stages.label`. That's
// the single source of truth — briefing_stages.label is what the
// producer's editor writes to, and per-day rows mean the embed
// resolves to the right day's label automatically.
//
// `record.stage_name` is a denormalised cache kept coherent by
// sync_stage_name_on_label_change. Used as a fallback for readers
// that haven't been migrated to the embed yet, and for display when
// the FK is unset (legacy rows whose stage_id never landed).
//
// Drop the fallback once all queries embed the FK and the
// `stage_name` column gets removed.

export interface StageLabelSource {
  stage_name?: string | null;
  briefing_stages?: { label?: string | null } | null;
}

export function getStageLabel(record: StageLabelSource | null | undefined): string {
  if (!record) return "";
  return record.briefing_stages?.label ?? record.stage_name ?? "";
}

/**
 * Is this the wizard's internal stage slug (`day0_stage1`) rather than a name?
 *
 * The slug is how the wizard keys stages before any briefing_stages row
 * exists. It is not a name and must never reach a producer: on HOH / 7TH 26
 * a copied zone that belonged to no stage fell through every label fallback
 * to its slug, and `day1_stage14` sat on the live festival map all weekend.
 * The write paths that produced such rows are fixed, but the rows already in
 * the database are not all repairable (a slug matches no stage label, so the
 * relink backfill passes them over) — so the display layer refuses the slug
 * too. Showing "Stage" is a smaller lie than showing our primary key.
 */
export function isInternalStageSlug(value: string | null | undefined): boolean {
  return !!value && /^day\d+_/.test(value);
}

/** The value, unless it is an internal slug — then nothing. */
function nameOrNothing(value: string | null | undefined): string | null {
  return isInternalStageSlug(value) ? null : (value ?? null);
}

// Same idea for festival_areas.name vs the FK-resolved label.
export interface AreaLabelSource {
  name?: string | null;
  area_type?: string | null;
  briefing_stages?: { label?: string | null } | null;
}

export function getAreaLabel(record: AreaLabelSource | null | undefined): string {
  if (!record) return "";
  if (record.area_type === "stage_zone") {
    // "Stage" rather than blank, matching resolveZoneLabel: a zone whose
    // only name is a slug still needs something to render.
    return record.briefing_stages?.label ?? nameOrNothing(record.name) ?? "Stage";
  }
  return record.name ?? "";
}
